import React, { useCallback, useEffect, useState } from 'react';
import useAuthGuard from '../hooks/useAuthGuard';
import Layout from '../components/Layout';
import { API_BASE_URL } from '../config';
import apiFetch from '../utils/apiFetch';

// store_chains は「人が書くものだけを持つ」テーブルなので、書く場所が要る。
// 店舗数・都道府県数・台の種類はここに出るが編集できない — 店舗から毎回
// 数え直しているもので、二重に持てば必ずずれる。
//
// published が false のチェーンはページが出ない。紹介文が書けるまで一覧にも
// 出さないほうが、空のページを晒すよりましである。

/** チェーンページの要約カード。空欄のカードは公開側で描画されない。 */
const NOTE_FIELDS = [
  { key: 'whereNote', label: 'どこにある？', placeholder: '例）イオンなどの商業施設内' },
  { key: 'playNote', label: 'どんな遊び？', placeholder: '例）クレーン・メダルゲーム' },
  { key: 'prizeNote', label: '景品の特徴', placeholder: '例）限定プライズの展開あり' },
];

const NOTE_MAX = 60;

export default function ChainEditor() {
  useAuthGuard();

  const [chains, setChains] = useState(null);
  const [error, setError] = useState('');
  const [openId, setOpenId] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/chains`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setChains((await res.json()).data || []);
    } catch (e) {
      setError(e.message);
      setChains([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  return (
    <Layout title="チェーン">
      <p className="text-muted mb-3" style={{ fontSize: '0.8125rem' }}>
        チェーンページに出す紹介文・要約カード・表紙写真を編集します。
        店舗数と台の種類は店舗から数え直しているので、ここでは変えられません。
      </p>

      {error && <div className="alert alert-danger">{error}</div>}
      {!chains && <div className="card"><div className="card-body text-center py-5">読み込み中...</div></div>}

      {chains && chains.map((chain) => (
        <ChainRow
          key={chain.id}
          chain={chain}
          open={openId === chain.id}
          onToggle={() => setOpenId(openId === chain.id ? '' : chain.id)}
          onSaved={load}
        />
      ))}
    </Layout>
  );
}

function ChainRow({ chain, open, onToggle, onSaved }) {
  const [form, setForm] = useState(() => pick(chain));
  const [photos, setPhotos] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');

  useEffect(() => { setForm(pick(chain)); }, [chain]);

  // 表紙の候補は開いたときだけ取りに行く。24チェーンぶんの写真を
  // 一覧の表示だけで読むと、見ない画像を毎回全部落とすことになる。
  useEffect(() => {
    if (!open || photos) return;
    (async () => {
      try {
        const res = await apiFetch(`${API_BASE_URL}/admin/chains/${chain.id}/photos`);
        setPhotos(res.ok ? (await res.json()).data || [] : []);
      } catch {
        setPhotos([]);
      }
    })();
  }, [open, photos, chain.id]);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const save = async (extra) => {
    setSaving(true);
    setMessage('');
    try {
      const res = await apiFetch(`${API_BASE_URL}/admin/chains/${chain.id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, ...extra }),
      });
      if (!res.ok) {
        const body = await res.json().catch(() => null);
        throw new Error(body?.error?.message || `HTTP ${res.status}`);
      }
      setMessage('保存しました');
      onSaved();
    } catch (e) {
      setMessage(`保存できませんでした: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  const filledNotes = NOTE_FIELDS.filter((f) => (chain[f.key] || '').trim()).length;

  return (
    <div className="card mb-2">
      <div className="card-body p-3">
        <div className="d-flex align-items-center gap-2">
          <button className="btn btn-sm btn-phoenix-secondary" onClick={onToggle}>
            {open ? '閉じる' : '編集'}
          </button>
          <strong style={{ fontSize: '0.9375rem' }}>{chain.name}</strong>
          <span className="text-muted" style={{ fontSize: '0.8125rem' }}>
            {chain.storeCount}店 / {chain.prefectureCount}都道府県
          </span>
          {!chain.published && <span className="badge bg-secondary">非公開</span>}
          {chain.heroPhotoUrl && <span className="badge bg-success">表紙あり</span>}
          <span className="badge bg-light text-dark">要約 {filledNotes}/3</span>
        </div>

        {open && (
          <div className="mt-3">
            <div className="row g-2">
              {NOTE_FIELDS.map((f) => (
                <div className="col-12 col-md-4" key={f.key}>
                  <label className="form-label mb-1" style={{ fontSize: '0.8125rem' }}>
                    {f.label}
                  </label>
                  <input
                    className="form-control form-control-sm"
                    maxLength={NOTE_MAX}
                    value={form[f.key]}
                    placeholder={f.placeholder}
                    onChange={(e) => set(f.key, e.target.value)}
                  />
                </div>
              ))}
            </div>
            <p className="text-muted mt-1 mb-3" style={{ fontSize: '0.75rem' }}>
              空欄のカードは公開ページに出ません。推測で埋めるより、書けるものだけ書いてください。
            </p>

            <div className="row g-2">
              <div className="col-12 col-md-6">
                <label className="form-label mb-1" style={{ fontSize: '0.8125rem' }}>運営会社</label>
                <input
                  className="form-control form-control-sm"
                  value={form.operator}
                  placeholder="確実なものだけ。推測なら空欄のまま"
                  onChange={(e) => set('operator', e.target.value)}
                />
              </div>
              <div className="col-12 col-md-6">
                <label className="form-label mb-1" style={{ fontSize: '0.8125rem' }}>公式サイト</label>
                <input
                  className="form-control form-control-sm"
                  value={form.websiteUrl}
                  placeholder="https://"
                  onChange={(e) => set('websiteUrl', e.target.value)}
                />
              </div>
            </div>

            <label className="form-label mb-1 mt-2" style={{ fontSize: '0.8125rem' }}>紹介文</label>
            <textarea
              className="form-control form-control-sm"
              rows={3}
              value={form.description}
              onChange={(e) => set('description', e.target.value)}
            />

            <div className="form-check mt-2">
              <input
                className="form-check-input"
                type="checkbox"
                id={`pub-${chain.id}`}
                checked={form.published}
                onChange={(e) => set('published', e.target.checked)}
              />
              <label className="form-check-label" htmlFor={`pub-${chain.id}`} style={{ fontSize: '0.8125rem' }}>
                公開する（外すとチェーンページも一覧も出なくなります）
              </label>
            </div>

            <div className="mt-3">
              <label className="form-label mb-1" style={{ fontSize: '0.8125rem' }}>
                表紙の写真
              </label>
              <p className="text-muted mb-2" style={{ fontSize: '0.75rem' }}>
                このチェーンの公開店舗の、承認済み写真から選びます。
                自動で選ぶとモールの入口や駐車場が顔になるので、人が決めます。
              </p>
              {photos === null && <div className="text-muted" style={{ fontSize: '0.8125rem' }}>読み込み中...</div>}
              {photos && photos.length === 0 && (
                <div className="text-muted" style={{ fontSize: '0.8125rem' }}>
                  選べる写真がありません（公開店舗に承認済みの写真が無い）
                </div>
              )}
              {photos && photos.length > 0 && (
                <div className="d-flex flex-wrap gap-2">
                  {form.heroPhotoId && (
                    <button
                      className="btn btn-sm btn-outline-secondary align-self-start"
                      disabled={saving}
                      onClick={() => { set('heroPhotoId', ''); save({ heroPhotoId: '' }); }}
                    >
                      表紙を外す
                    </button>
                  )}
                  {photos.map((p) => (
                    <button
                      key={p.id}
                      title={p.storeName}
                      disabled={saving}
                      onClick={() => { set('heroPhotoId', p.id); save({ heroPhotoId: p.id }); }}
                      style={{
                        padding: 0,
                        border: form.heroPhotoId === p.id ? '3px solid #0d6efd' : '1px solid #dee2e6',
                        borderRadius: 4,
                        background: 'none',
                        lineHeight: 0,
                      }}
                    >
                      <img
                        src={p.photoUrl}
                        alt=""
                        style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 2 }}
                      />
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div className="d-flex align-items-center gap-2 mt-3">
              <button className="btn btn-primary btn-sm" disabled={saving} onClick={() => save()}>
                {saving ? '保存中...' : '保存'}
              </button>
              {message && <span className="text-muted" style={{ fontSize: '0.8125rem' }}>{message}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

/** 送信するフィールドだけを取り出す。数えて出しているものは含めない。 */
function pick(chain) {
  return {
    whereNote: chain.whereNote || '',
    playNote: chain.playNote || '',
    prizeNote: chain.prizeNote || '',
    operator: chain.operator || '',
    websiteUrl: chain.websiteUrl || '',
    description: chain.description || '',
    published: Boolean(chain.published),
    heroPhotoId: chain.heroPhotoId || '',
  };
}
